import React from 'react';
import { withTheme } from 'styled-components';
import { ArrowDown, ArrowUp } from '@rsos/assets/icons';
import { CaretIcon } from '@rsos/assets/icons';
import { Box, Button, Select, Text } from '@rsos/components/capstone/base';
import { ChevronContainer } from '@rsos/components/capstone/base/Select/Select.styles';
import {
  trackClickOnFloorArrow,
  trackFloorSelector,
} from '@rsos/components/capstone/common/Map/SpatialLayers/spatialLayersTrackings';

export const CRGDropdownIndicator = props => {
  return (
    <ChevronContainer
      style={{
        transform: `rotate(${props.selectProps.menuIsOpen ? -180 : 0}deg)`,
        cursor: props.isDisabled ? 'not-allowed' : 'pointer',
      }}
      data-name="select-arrow"
    >
      <CaretIcon width={7} height={11} />
    </ChevronContainer>
  );
};
const customComponents = {
  DropdownIndicator: CRGDropdownIndicator,
};
/**
 * Component for displaying the floor selector buttons and dropdown
 * @param {Object} theme - The theme's colors for styling. The Select component
 * requires it as a prop. This component is rendered via Google Maps. It doesn't
 * have context into the theme even though Select is exported with withTheme()
 * @param {Array} floorOptions - list of options for the Select component
 * @param {String} irpVersion - used for gainsight analytics
 * @param {Boolean} isDisabled - flag for disabling the Select
 * @param {Function} onOptionChange - onChange handler for the floor selector
 * @param {String} source - Source of spatial layer/object
 */
export const FloorSelector = withTheme(
  ({
    floorOptions,
    irpVersion,
    isDisabled,
    onOptionChange,
    source,
    ...props
  }) => {
    const theme = props.theme;
    const selectStyles = {
      clearIndicator: styles => ({
        ...styles,
        display: 'none', // hide the `x` icon since search is disabled for this phase
      }),
      container: styles => ({
        ...styles,
        height: 25,
        ...(isDisabled
          ? {
              pointerEvents: 'auto',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
            }
          : {}),
        zIndex: 2,
      }),
      control: styles => ({
        ...styles,
        borderColor: theme.colors.heavyLineOutline,
        ...(isDisabled
          ? {
              pointerEvents: 'auto',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
            }
          : {}),
        maxHeight: 25,
        minHeight: 20,
        width: 150,
      }),
      indicatorsContainer: styles => ({
        ...styles,
        height: 25,
        width: 30,
      }),
      valueContainer: styles => ({
        ...styles,
        height: 25,
      }),
    };

    const selectedFloorIndex = floorOptions.findIndex(
      floor => floor.isSelected,
    );

    const handleClickUp = () => {
      if (selectedFloorIndex < floorOptions.length - 1) {
        trackClickOnFloorArrow('up', irpVersion, source);
        handleOptionChange(floorOptions[selectedFloorIndex + 1]);
      }
    };

    const handleClickDown = () => {
      if (selectedFloorIndex > 0) {
        trackClickOnFloorArrow('down', irpVersion, source);
        handleOptionChange(floorOptions[selectedFloorIndex - 1]);
      }
    };

    function handleOptionChange(option) {
      onOptionChange(option.data);
    }

    const handleSelectChange = option => {
      trackFloorSelector(irpVersion, source);
      handleOptionChange(option);
    };

    return (
      <>
        <Box horizontal color="primaryText">
          <Button
            btnType="tertiary"
            disabled={!floorOptions.length || selectedFloorIndex === 0}
            size="xsmall"
            noMargin
            mr="9px"
            onClick={handleClickDown}
          >
            <ArrowDown color={theme.colors.secondaryLink} />
          </Button>
          <Button
            btnType="tertiary"
            disabled={
              !floorOptions.length ||
              !(selectedFloorIndex < floorOptions.length - 1)
            }
            size="xsmall"
            noMargin
            mr="9px"
            onClick={handleClickUp}
          >
            <ArrowUp color={theme.colors.secondaryLink} />
          </Button>
          <Select
            components={customComponents}
            isDisabled={!floorOptions.length}
            isSearchable={false}
            onChange={handleSelectChange}
            options={floorOptions}
            placeholder={
              <Text color="secondaryText" size="small">
                No floors available
              </Text>
            }
            styles={selectStyles}
            theme={theme}
            value={floorOptions[selectedFloorIndex]}
          />
        </Box>
      </>
    );
  },
);
